import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Table,
  Typography,
} from '@mui/joy';
import type { DooHardwareCollection, StatusFilter } from '../types';
import {
  createHardwareCollection,
  deleteHardwareCollection,
  formatDependents,
  hardwareCollectionDependents,
  listHardwareCollections,
  updateHardwareCollection,
} from '../data/repository';
import { catalogueStatus, matchesStatusFilter } from '../data/status';
import { useAsyncList } from './NamedEntitySection';
import {
  ConfirmDialog,
  DivestFields,
  StatusChip,
  StatusFilterSelect,
  Toolbar,
} from './FormControls';

export function HardwareCollectionsSection() {
  const list = useAsyncList(listHardwareCollections);
  const [filter, setFilter] = useState<StatusFilter>('active');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DooHardwareCollection | null>(null);
  const [name, setName] = useState('');
  const [divest, setDivest] = useState(false);
  const [deprecatedDate, setDeprecatedDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteRow, setDeleteRow] = useState<DooHardwareCollection | null>(null);

  const visible = useMemo(
    () => list.rows.filter((row) => matchesStatusFilter(row, filter)),
    [list.rows, filter]
  );

  const payload = () => ({
    collection_name: name.trim(),
    divest,
    deprecated_date: deprecatedDate || null,
  });

  return (
    <Box>
      {list.error ? <Alert color="danger" sx={{ mb: 2 }}>{list.error}</Alert> : null}
      {formError && !open ? <Alert color="warning" sx={{ mb: 2 }}>{formError}</Alert> : null}
      <Toolbar>
        <StatusFilterSelect value={filter} onChange={setFilter} />
        <Button
          onClick={() => {
            setEditing(null);
            setName('');
            setDivest(false);
            setDeprecatedDate('');
            setFormError(null);
            setOpen(true);
          }}
        >
          Add collection
        </Button>
      </Toolbar>
      <Sheet variant="outlined" sx={{ overflow: 'auto' }}>
        <Table stickyHeader>
          <thead>
            <tr>
              <th>Collection</th>
              <th>Status</th>
              <th>Deprecated Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.loading ? (
              <tr><td colSpan={4}>Loading…</td></tr>
            ) : (
              visible.map((row) => (
                <tr key={row.collection_id}>
                  <td>{row.collection_name}</td>
                  <td><StatusChip status={catalogueStatus(row)} /></td>
                  <td>{row.deprecated_date?.slice(0, 10) || '—'}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="plain"
                      onClick={() => {
                        setEditing(row);
                        setName(row.collection_name);
                        setDivest(Boolean(row.divest));
                        setDeprecatedDate(row.deprecated_date?.slice(0, 10) || '');
                        setOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={async () => {
                        const dependents = await hardwareCollectionDependents(row.collection_id);
                        if (dependents.length) {
                          setFormError(
                            `Cannot delete “${row.collection_name}” while it is used by ${formatDependents(dependents)}. Divest or deprecate it instead.`
                          );
                          return;
                        }
                        setDeleteRow(row);
                      }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ minWidth: 440 }}>
          <ModalClose />
          <Typography level="title-lg">{editing ? 'Edit collection' : 'New collection'}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {formError ? <Alert color="danger">{formError}</Alert> : null}
            <Input placeholder="Collection name" value={name} onChange={(e) => setName(e.target.value)} />
            <DivestFields
              divest={divest}
              deprecatedDate={deprecatedDate}
              onDivest={setDivest}
              onDeprecatedDate={setDeprecatedDate}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!name.trim()) {
                    setFormError('Collection name is required.');
                    return;
                  }
                  try {
                    if (editing) await updateHardwareCollection(editing.collection_id, payload());
                    else await createHardwareCollection(payload());
                    setOpen(false);
                    list.reload();
                  } catch (err) {
                    setFormError(err instanceof Error ? err.message : 'Save failed');
                  }
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteRow)}
        title="Delete collection?"
        message={deleteRow ? `Delete “${deleteRow.collection_name}”? Prefer deprecation for established ranges.` : ''}
        onClose={() => setDeleteRow(null)}
        onConfirm={async () => {
          if (!deleteRow) return;
          try {
            await deleteHardwareCollection(deleteRow.collection_id);
            setDeleteRow(null);
            list.reload();
          } catch (err) {
            setDeleteRow(null);
            setFormError(err instanceof Error ? err.message : 'Delete failed');
          }
        }}
      />
    </Box>
  );
}
